import React, { useEffect, useState } from 'react';
import { array, string, bool, func, shape } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { translate } from 'react-i18next';
import classnames from 'classnames/bind';
import { fromEvent } from 'rxjs';
import { map, pairwise, distinctUntilChanged, tap } from 'rxjs/operators';

import { BaseHeader } from '@snapask/common-components';
import Link from '@blueberry/components/Link';

import PageListHeader from '../PageListHeader';

import { getProfileInfo, getRole } from '@blueberry/redux/profile';
import {
  getZendeskFAQLink,
  getOfferingList,
  getMultipleOfferingMode,
  getCampaignLinks,
  getShowLiveInstructorBtn,
  getShowCourseInstructorBtn,
} from '@blueberry/redux/siteInfo';
import { actions } from '@blueberry/redux/auth';
import {
  generateOfficialSiteURL,
  generateAcademyURL,
  generateQuestionAskURL,
} from '@blueberry/redux/navigation';

import { sendGTMEvent } from '@blueberry/redux/googleTagManager';
import { getRegion } from '@blueberry/redux/locale';

import {
  NAV_RC_ALL_INSTRUCTOR_CLICK,
  NAV_COURSE_ALL_INSTRUCTOR_CLICK,
  HOME_DOWNLOAD_CLICK,
} from '@blueberry/constants/gtmEvents';
import {
  COURSE_INSTRUCTOR_LIST,
  REGULAR_CLASS_INSTRUCTOR_LIST,
} from '@blueberry/constants/navigation';
import { ANDROID, IOS } from '@blueberry/constants/appDownloadType';

import styles from './SnapaskHeader.scss';

const cx = classnames.bind(styles);

function SnapaskHeader({
  appRegion,
  getAcademyURL,
  getOfficialSiteURL,
  getQuestionAskURL,
  isAuthenticated,
  handleClickLogout,
  userInfo,
  offerings,
  t,
  zendeskFAQUrl,
  multipleOfferingMode,
  campaignLinks,
  showBaseHeader,
  showAcademyHeader,
  showCourseInstructorBtn,
  showLiveInstructorBtn,
  sendGTMEvent,
}) {
  const [onWindowTop, setOnWindowTop] = useState(window.pageYOffset === 0);
  const [hideBaseHeader, setHideBaseHeader] = useState(false);

  const showBothHeader = showBaseHeader && showAcademyHeader;

  useEffect(() => {
    if (!showBothHeader) return;

    const scrolling$ = fromEvent(document, 'scroll')
      .pipe(
        map(() => window.pageYOffset),
        // Safari pageYOffset could be negative number
        tap(currentY => setOnWindowTop(currentY <= 0)),
        pairwise(),
        map(([y1, y2]) => (y2 < y1 ? 'Up' : 'Down')),
        distinctUntilChanged()
      )
      .subscribe(direction => {
        setHideBaseHeader(!onWindowTop && direction === 'Down');
      });
    return () => scrolling$.unsubscribe();
  }, [onWindowTop, showBothHeader]);

  const onClickNavItem = key => {
    const eventNameMapping = {
      [REGULAR_CLASS_INSTRUCTOR_LIST]: NAV_RC_ALL_INSTRUCTOR_CLICK,
      [COURSE_INSTRUCTOR_LIST]: NAV_COURSE_ALL_INSTRUCTOR_CLICK,
    };

    sendGTMEvent({
      eventName: eventNameMapping[key],
    });
  };

  const onIosDownloadClick = () => {
    sendGTMEvent({
      eventName: HOME_DOWNLOAD_CLICK,
      data: {
        region: appRegion,
        type: IOS,
      },
    });
  };

  const onAndroidDownloadClick = () => {
    sendGTMEvent({
      eventName: HOME_DOWNLOAD_CLICK,
      data: {
        region: appRegion,
        type: ANDROID,
      },
    });
  };

  return (
    <React.Fragment>
      {showBaseHeader && (
        <BaseHeader
          getAcademyURL={getAcademyURL}
          getOfficialSiteURL={getOfficialSiteURL}
          getQuestionAskURL={getQuestionAskURL}
          LinkComponent={Link}
          isAuthenticated={isAuthenticated}
          onClickLogout={handleClickLogout}
          userInfo={userInfo}
          zendeskFAQUrl={zendeskFAQUrl}
          offerings={offerings}
          multipleOfferingMode={multipleOfferingMode}
          campaignLinks={campaignLinks}
          showCourseInstructorLink={showCourseInstructorBtn}
          showLiveInstructorLink={showLiveInstructorBtn}
          t={t}
          onClick={onClickNavItem}
          onIosDownloadClick={onIosDownloadClick}
          onAndroidDownloadClick={onAndroidDownloadClick}
        />
      )}
      {showAcademyHeader && (
        <div
          className={cx(styles.academyHeader, {
            noBackground: onWindowTop,
            fixedTop: hideBaseHeader,
          })}
        >
          <PageListHeader textLogo={!hideBaseHeader} />
        </div>
      )}
    </React.Fragment>
  );
}

SnapaskHeader.propTypes = {
  appRegion: string.isRequired,
  campaignLinks: shape({
    regularClass: array,
    course: array,
    activity: array,
  }).isRequired,
  getAcademyURL: func.isRequired,
  getOfficialSiteURL: func.isRequired,
  getQuestionAskURL: func.isRequired,
  handleClickLogout: func.isRequired,
  isAuthenticated: bool.isRequired,
  multipleOfferingMode: bool.isRequired,
  offerings: array.isRequired,
  sendGTMEvent: func.isRequired,
  showAcademyHeader: bool.isRequired,
  showBaseHeader: bool.isRequired,
  showCourseInstructorBtn: bool.isRequired,
  showLiveInstructorBtn: bool.isRequired,
  t: func.isRequired,
  zendeskFAQUrl: string.isRequired,
  userInfo: shape({
    avatarUrl: string,
    username: string,
    role: string,
    roleStatus: string,
  }),
};

export default compose(
  translate(['navigation']),
  connect(
    state => ({
      isAuthenticated: !!state.auth.token,
      userInfo: {
        avatarUrl: getProfileInfo(state, 'avatar'),
        username: getProfileInfo(state, 'username'),
        role: getRole(state),
        roleStatus: getProfileInfo(state, 'roleStatus'),
      },
      zendeskFAQUrl: getZendeskFAQLink(state),
      offerings: getOfferingList(state),
      multipleOfferingMode: getMultipleOfferingMode(state),
      getAcademyURL: generateAcademyURL(state),
      getOfficialSiteURL: generateOfficialSiteURL(state),
      getQuestionAskURL: generateQuestionAskURL(state),
      campaignLinks: getCampaignLinks(state),
      showLiveInstructorBtn: getShowLiveInstructorBtn(state),
      showCourseInstructorBtn: getShowCourseInstructorBtn(state),
      appRegion: getRegion(state),
    }),
    {
      handleClickLogout: actions.logout,
      sendGTMEvent,
    }
  )
)(SnapaskHeader);



// WEBPACK FOOTER //
// ./app/components/SnapaskHeader/SnapaskHeader.jsx